import { DocumentNode, useMutation } from "@apollo/client";

import { gql } from "~/__generated__";
import { parseDateToString } from "~/modules/app/util/dateHelpers";

import { FeatureCreateOption } from "../components/MapEditorFeatureCreateDialog/components/types";
import { MapEditorGetAllLayerFeaturesDocument } from "./useMapEditorGetAllLayerFeatures.generated";

const MAP_EDITOR_OBSERVATION_CREATE = gql(`
mutation ObservationCreate($input: ObservationCreateInput!) {
    observationCreate(input: $input) {
        id
        feature {
            id
            layerId
        }
    }
}
`);

const MAP_EDITOR_PLANNERAREA_CREATE = gql(`
mutation PlannerV2PlannerAreaCreate($input: PlannerAreaCreateInput!) {
    plannerV2PlannerAreaCreate(input: $input) {
        id
        mapEditorFeature {
            id
            layerId
        }
    }
}
`);

const MAP_EDITOR_ECOLOGICAL_FUNCTION_CREATE = gql(`
mutation EcologicalFunctionCreate($input: GqlEcologicalFunctionCreateInput!) {
    ecologicalFunctionCreate(input: $input) {
        id
        feature {
            id
            layerId
        }
    }
}
`);

const MAP_EDITOR_OTHER_CREATE = gql(`
    mutation MapEditorOtherFeatureCreate($input: MapEditorOtherFeatureCreateInput!) {
        mapEditorOtherFeatureCreate(input: $input) {
            id
            layerId
        }
    }
`);

type MapEditorFeatureCreateInput = {
    geometry: GeoJSON.Geometry;
    projectId: string;
    mapEditorLayerId: string;
    options: FeatureCreateOption;
};

export default function useMapEditorFeatureCreate(featureMutateRefetchQueries: DocumentNode[], visitId?: string) {
    const [observationCreate, { loading: loadingObservation, error: errorObservation }] = useMutation(
        MAP_EDITOR_OBSERVATION_CREATE,
        {
            refetchQueries: [
                MapEditorGetAllLayerFeaturesDocument,

                ...featureMutateRefetchQueries,
            ],
        },
    );
    const [plannerAreaCreate, { loading: loadingArea, error: errorArea }] = useMutation(MAP_EDITOR_PLANNERAREA_CREATE, {
        refetchQueries: [MapEditorGetAllLayerFeaturesDocument],
    });
    const [ecologicalFunctionCreate, { loading: loadingEcologicalFunction, error: errorEcologicalFunction }] = useMutation(
        MAP_EDITOR_ECOLOGICAL_FUNCTION_CREATE,
        {
            refetchQueries: [MapEditorGetAllLayerFeaturesDocument],
        },
    );

    const [otherFeatureCreate, { loading: loadingOther, error: errorOther }] = useMutation(MAP_EDITOR_OTHER_CREATE, {
        refetchQueries: [MapEditorGetAllLayerFeaturesDocument],
    });

    return {
        featureCreate(input: MapEditorFeatureCreateInput) {
            switch (input.options.type) {
                case "area":
                    return plannerAreaCreate({
                        variables: {
                            input: {
                                geometry: input.geometry,
                                mapEditorLayerId: input.mapEditorLayerId,
                                projectId: input.projectId,
                                protocolIds: input.options.protocols?.map(p => p.id) ?? [],
                            },
                        },
                    }).then(result => result.data?.plannerV2PlannerAreaCreate?.mapEditorFeature.id);
                case "observation":
                    return observationCreate({
                        variables: {
                            input: {
                                geometry: input.geometry,
                                mapEditorLayerId: input.mapEditorLayerId,
                                observationTypeId: input.options.observationType.id,
                                projectId: input.projectId,
                                researchTypeId: input.options.researchType.id,
                                speciesId: input.options.species.id,
                                plannerVisitId: visitId, // TODO: Remove when legacy ProjectReviewVisitScreen is deleted
                                createdAt: parseDateToString(new Date()),
                            },
                        },
                    }).then(result => result.data?.observationCreate?.feature.id);
                case "ecologicalFunction":
                    return ecologicalFunctionCreate({
                        variables: {
                            input: {
                                geometry: input.geometry,
                                mapEditorLayerId: input.mapEditorLayerId,
                                ecologicalFunctionTypeId: input.options.ecologicalFunctionType.id,
                                projectId: input.projectId,
                                researchTypeId: input.options.researchType.id,
                                speciesIds: [input.options.species.id],
                                plannerVisitId: visitId,
                                createdAt: parseDateToString(new Date()),
                            },
                        },
                    }).then(result => result.data?.ecologicalFunctionCreate?.feature.id);
                case "other":
                    return otherFeatureCreate({
                        variables: {
                            input: {
                                geometry: input.geometry,
                                mapEditorLayerId: input.mapEditorLayerId,
                            },
                        },
                    }).then(result => result.data?.mapEditorOtherFeatureCreate?.id);
                case "none":
                    return;
            }
        },
        loading: loadingObservation || loadingArea || loadingEcologicalFunction || loadingOther,
        error: errorObservation || errorArea || errorEcologicalFunction || errorOther,
    };
}
